import CategorySelector from '@/modules/cashFlow/components/CategorySelector';
import {
  BankTransaction,
  useUpdateTransactionMutation,
} from '@/store/transactionsApi';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SingleDate from '@/components/date/SingleDate';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatOptionalDayjs } from '@/utils/date';
import dayjs from '@/config/dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import MakeRuleFromTransaction, {
  MakeRuleInlineValue,
} from '@/modules/cashFlow/MakeRuleFromTransaction';

interface EditTransactionModalProps {
  open: boolean;
  onClose: () => void;
  transaction: BankTransaction | null;
  investorId?: number;
  onUpdated?: (updated: BankTransaction) => void;
}

const EditTransactionModal: React.FC<EditTransactionModalProps> = ({
  open,
  onClose,
  transaction,
  investorId,
  onUpdated,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [description, setDescription] = useState<string>('');
  const [date, setDate] = useState<string>(''); // YYYY-MM-DD
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');
  const [categoryDirty, setCategoryDirty] = useState<boolean>(false);
  const [initialDescription, setInitialDescription] = useState<string>('');
  const [initialCategoryId, setInitialCategoryId] = useState<number | ''>('');
  const [ruleValue, setRuleValue] = useState<MakeRuleInlineValue>({
    alwaysChange: false,
    comparisonOperator: 1,
    comparisonText: '',
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  const [updateTransaction, { isLoading: isSaving }] =
    useUpdateTransactionMutation();

  useEffect(() => {
    if (open && transaction) {
      setDescription(transaction.DisplayDescription || '');
      const d =
        formatOptionalDayjs(transaction.TransactionDate, 'YYYY-MM-DD', '') ||
        dayjs().format('YYYY-MM-DD');
      setDate(d);
      // Initialize category selector to current tx category
      setSelectedCategoryId(transaction.BankTransactionCategoryId ?? '');
      setInitialDescription(transaction.DisplayDescription || '');
      setInitialCategoryId(transaction.BankTransactionCategoryId ?? '');
      setCategoryDirty(false);
      setRuleValue({
        alwaysChange: false,
        comparisonOperator: 1,
        comparisonText:
          transaction.OriginalDescription ||
          transaction.DisplayDescription ||
          '',
      });
    }
    if (!open) {
      setSnackbar(null);
    }
  }, [open, transaction]);

  const handleSave = async () => {
    if (!transaction) return;

    // Format to ISO to mirror legacy payloads that include time components
    const isoDate = dayjs(date).toISOString();

    // Start from preserved original fields explicitly (legacy parity)
    const payload: Partial<BankTransaction> = {
      DisplayDescription: description,
      TransactionDate: isoDate,
      // Legacy also sends a Date field alongside TransactionDate
      // @ts-expect-error: backend accepts this field even if not typed in BankTransaction
      Date: isoDate as unknown as string,
      // Include identifiers for full parity
      WealthAccountTransactionId: transaction.WealthAccountTransactionId,
      WealthAccountId: transaction.WealthAccountId,
      // Preserve and explicitly include category display fields unless changed
      CategoryName: transaction.CategoryName,
      ParentCategoryId: transaction.ParentCategoryId,
      ParentCategoryName: transaction.ParentCategoryName,
      // Preserve rule comparator fields (legacy includes these even when not changing rules)
      ComparisonOperator: transaction.ComparisonOperator,
      ComparisonText: transaction.ComparisonText,
      // Flags aligned with editing via modal (not a rule creation path)
      AlwaysChange: ruleValue.alwaysChange,
      CanEdit: transaction.CanEdit,
      TypeManuallyChanged: true,
      TypeChangedByRule: transaction.TypeChangedByRule,
      OriginalDescription: transaction.OriginalDescription,
      CashflowGroup: transaction.CashflowGroup,
      IsCustomCategory: transaction.IsCustomCategory,
      // Keep other stable display fields
      Name: transaction.Name,
      Amount: transaction.Amount,
      AccountNumber: transaction.AccountNumber,
    };

    // Always send BankTransactionCategoryId for parity; use original unless user changed it
    if (categoryDirty && selectedCategoryId !== '') {
      payload.BankTransactionCategoryId = selectedCategoryId;
      // Preserve original names/parent fields for legacy parity; server derives names from ID
      payload.ParentCategoryId = transaction.ParentCategoryId;
      payload.ParentCategoryName = transaction.ParentCategoryName;
      payload.CategoryName = transaction.CategoryName;
    } else {
      payload.BankTransactionCategoryId = transaction.BankTransactionCategoryId;
      // preserve original names as they existed on the transaction
      payload.ParentCategoryId = transaction.ParentCategoryId;
      payload.ParentCategoryName = transaction.ParentCategoryName;
      payload.CategoryName = transaction.CategoryName;
    }

    // If user opted to always change, populate comparator fields from inline state
    if (ruleValue.alwaysChange) {
      payload.ComparisonOperator = ruleValue.comparisonOperator;
      payload.ComparisonText =
        ruleValue.comparisonOperator === 1
          ? transaction.OriginalDescription ||
            transaction.DisplayDescription ||
            ''
          : ruleValue.comparisonText || transaction.DisplayDescription || '';
    }

    try {
      await updateTransaction({
        transactionId: transaction.WealthAccountTransactionId,
        transaction: payload,
      }).unwrap();

      const updated: BankTransaction = {
        ...transaction,
        ...payload,
        // Update display names locally for immediate UI feedback
        CategoryName: payload.CategoryName ?? transaction.CategoryName,
        ParentCategoryName:
          payload.ParentCategoryName ?? transaction.ParentCategoryName,
      };

      onUpdated?.(updated);
      setSnackbar({
        open: true,
        message:
          t('transactions.updated') || 'Transaction updated successfully!',
        severity: 'success',
      });
      onClose();
    } catch {
      setSnackbar({
        open: true,
        message:
          t('transactions.updateFailed') || 'Failed to update transaction.',
        severity: 'error',
      });
    }
  };

  const showAlwaysChange = useMemo(() => {
    if (!transaction) return false;
    const hasOriginal = Boolean(
      transaction.OriginalDescription && transaction.OriginalDescription.length,
    );
    const descriptionChanged = description !== initialDescription;
    const categoryChanged = selectedCategoryId !== initialCategoryId;
    return hasOriginal && (descriptionChanged || categoryChanged);
  }, [
    transaction,
    description,
    initialDescription,
    selectedCategoryId,
    initialCategoryId,
  ]);

  const handleCloseSnackbar = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') return;
    setSnackbar(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={isSaving ? undefined : onClose}
        disableEscapeKeyDown={isSaving}
        fullWidth
        maxWidth='sm'
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            ...(isMobile
              ? {
                  pt: 'calc(16px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))',
                }
              : {}),
          }}
        >
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            sx={{ flexGrow: 1 }}
          >
            <EditIcon fontSize='small' />
            <Typography variant='h6'>
              {t('transactions.editTransaction') || 'Edit Transaction'}
            </Typography>
          </Stack>
          <Button
            onClick={onClose}
            color='inherit'
            sx={{
              minWidth: 0,
              padding: 1,
              borderRadius: '50%',
            }}
            disabled={isSaving}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent
          dividers
          sx={isMobile ? { px: 2 } : {}}
        >
          <Stack spacing={2}>
            <TextField
              label={t('description') || 'Description'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size='small'
              required
              disabled={isSaving}
            />

            <CategorySelector
              label={t('addTransactionModal.categoryLabel')}
              value={selectedCategoryId}
              onChange={(id) => {
                setSelectedCategoryId(id);
                setCategoryDirty(true);
              }}
              disabled={isSaving}
              investorId={investorId}
            />

            <Box sx={{ width: '100%' }}>
              <SingleDate
                date={date ? dayjs(date).format('MM/DD/YYYY') : ''}
                onChange={(newDate: string) => {
                  if (newDate) {
                    const [month, dayStr, year] = newDate.split('/');
                    setDate(
                      `${year}-${month.padStart(2, '0')}-${dayStr.padStart(2, '0')}`,
                    );
                  }
                }}
                label={t('date') || 'Date'}
                showShortcuts={false}
                disabled={isSaving}
              />
            </Box>

            {showAlwaysChange && transaction && (
              <MakeRuleFromTransaction
                transaction={transaction}
                currentDescription={description}
                currentCategoryId={selectedCategoryId}
                savedCategoryId={initialCategoryId}
                investorId={investorId}
                isSaving={isSaving}
                value={ruleValue}
                onChange={setRuleValue}
              />
            )}

            {transaction && (
              <Box>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary' }}
                >
                  {t('grid.amount') || t('amount') || 'Amount'}
                </Typography>
                <Typography variant='body1'>
                  {formatNumberToCurrency(transaction.Amount)}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            ...(isMobile
              ? {
                  pb: 'calc(16px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
                }
              : {}),
          }}
        >
          <Button
            onClick={onClose}
            variant='outlined'
            size='large'
            disabled={isSaving}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSave}
            variant='contained'
            size='large'
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : t('save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {snackbar && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default EditTransactionModal;
